import axios from "axios";
import { getToken } from "../utils/constants";
import store from "../redux/store";
import { emitSessionExpired } from "../utils/postMessageBridge";

const { dispatch } = store;

export const AXIOS_INSTANCE_WITHOUT_INTERCEPTOR = axios.create();

AXIOS_INSTANCE_WITHOUT_INTERCEPTOR.interceptors.request.use(async (config) => {
  const token = localStorage.getItem("token");

  let operatorId;
  try {
    operatorId = JSON.parse(localStorage.getItem("operatorId"));
  } catch {
    operatorId = localStorage.getItem("operatorId");
  }

  if (operatorId) {
    config.headers["OperatorId"] = operatorId;
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const handleSessionExpiry = () => {
  emitSessionExpired();

  // Read before clearing — localStorage.clear() wipes everything
  const isStandalone = localStorage.getItem("standaloneMode") === "true";
  const authDetails = JSON.parse(localStorage.getItem("authDetails"));

  localStorage.clear();
  dispatch({ type: "accountData/logOut", payload: {} });
  dispatch({ type: "gameData/logOut", payload: {} });

  if (!isStandalone) {
    if (authDetails?.homeUrl) {
      window.location.href = authDetails.homeUrl;
    } else {
      window.location.href = "/un-auth";
    }
  }
};

AXIOS_INSTANCE_WITHOUT_INTERCEPTOR.interceptors.response.use(
  (response) => {
    const token = getToken();

    if (response.headers.authorization && token) {
      const newToken = response.headers.authorization.replace("Bearer ", "");
      localStorage.setItem("token", newToken);
      dispatch({
        type: "accountData/setToken",
        payload: newToken,
      });
    }

    // Backend sometimes returns HTTP 200 with session-expired code in body.
    // PostPatchResponse format: { code: 401, response: "..." }
    const bodyCode = response?.data?.code;
    if (bodyCode === 401) {
      handleSessionExpiry();
      return Promise.reject(new Error("Session expired"));
    }

    return response;
  },
  (error) => {
    if (error?.response?.status === 401) {
      handleSessionExpiry();
    }
    return Promise.reject(error);
  }
);

// Retry interceptor for transient failures on GET requests only.
// POST/PUT/DELETE are never retried to avoid duplicating side effects (e.g. double bets).
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // exponential backoff

AXIOS_INSTANCE_WITHOUT_INTERCEPTOR.interceptors.response.use(null, async (error) => {
  const config = error.config;
  if (!config) return Promise.reject(error);

  // Only retry GET requests
  if (config.method && config.method.toUpperCase() !== "GET") {
    return Promise.reject(error);
  }

  // Don't retry auth errors
  if (error?.response?.status === 401 || error?.response?.status === 403) {
    return Promise.reject(error);
  }

  // Track retry count
  config.__retryCount = config.__retryCount || 0;
  if (config.__retryCount >= MAX_RETRIES) {
    return Promise.reject(error);
  }

  config.__retryCount += 1;
  const delay = RETRY_DELAYS[config.__retryCount - 1] || 4000;

  await new Promise((resolve) => setTimeout(resolve, delay));
  return AXIOS_INSTANCE_WITHOUT_INTERCEPTOR(config);
});

export const AXIOS_INSTANCE = AXIOS_INSTANCE_WITHOUT_INTERCEPTOR;
